import { useEffect, useState, useCallback } from "react";
import { downloadInvoice, fetchJobInvoicesContent } from "../service";
import { formatMoney } from "../../../utils";
import DivSpinner from "../../../components/divSpinner";
import BtnSpinner from "../../../components/btnSpinner";
import { theme } from "../../../theme";

const INVOICE_TYPE = "INSTAFIX_TO_CUSTOMER_PAYMENT_INVOICE";

const InstafixToCustomer = ({ job_id }) => {
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState(null);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const getInvoicesContent = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchJobInvoicesContent(
                job_id,
                INVOICE_TYPE
            );
            if (response) {
                setContent(response.data);
            }
        } catch (error) {
            console.error("Error fetching invoice content:", error);
        } finally {
            setLoading(false);
        }
    }, [job_id]);

    useEffect(() => {
        if (job_id) {
            getInvoicesContent();
        }
    }, [job_id, getInvoicesContent]);

    const handleDownload = useCallback(async () => {
        try {
            setDownloadBtnLoading(true);
            await downloadInvoice(job_id, INVOICE_TYPE);
        } catch (error) {
            console.error("Error downloading invoice:", error);
        } finally {
            setDownloadBtnLoading(false);
        }
    }, [job_id]);

    if (loading) {
        return (
            <div style={{ height: `${window.innerHeight - 387}px` }}>
                <DivSpinner />
            </div>
        );
    }

    if (!content) return null;

    const {
        company,
        customer,
        skill,
        job_address,
        job_id: invoiceNo,
        invoice_date,
        job_createdAt,
        job_booking_fee,
        job_call_out_fee,
        subtotal,
        vat_amount,
        total,
    } = content;

    return (
        <div className="container-fluid my-3 py-3">
            <div className="row">
                <div className="col-md-10 col-lg-8 col-sm-10 mx-auto">
                    <form className="" action="index.html" method="post">
                        <div className="card my-sm-2">
                            <div className="card-header text-center">
                                <div className="row justify-content-between">
                                    <div className="col-md-4 text-start">
                                        <img
                                            className="mb-2 w-50 p-2"
                                            src={`/assets/img/instafix-logo-1.png`}
                                            alt="Logo"
                                        />
                                        <h6 className="mb-0">
                                            {company?.company_address}
                                        </h6>
                                        <p className="d-block text-secondary mb-0">
                                            Email: {company?.company_email}
                                        </p>
                                        <p className="d-block text-secondary">
                                            Phone: {company?.company_contact_number}
                                        </p>
                                    </div>

                                    <div className="col-lg-3 col-md-7 text-md-end text-start mt-5">
                                        <h6 className="d-block mt-2 mb-0">
                                            {customer?.full_name}
                                        </h6>
                                        <p className="text-secondary mb-0">
                                            Email: {customer?.email}
                                        </p>
                                        <p className="text-secondary">
                                            Phone: {customer?.contact_number}
                                        </p>
                                    </div>
                                </div>

                                <br />

                                <div className="row justify-content-md-between">
                                    <div className="col-md-4 mt-auto">
                                        <h6 className="mb-0 text-start text-secondary font-weight-normal">
                                            Invoice no
                                        </h6>
                                        <h5 className="text-start mb-0">
                                            #{invoiceNo}
                                        </h5>
                                    </div>

                                    <div className="col-lg-5 col-md-7 mt-auto">
                                        <div className="row mt-md-5 mt-4 text-md-end text-start">
                                            <div className="col-md-6">
                                                <h6 className="text-secondary font-weight-normal mb-0">
                                                    Invoice date:
                                                </h6>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="text-dark mb-0">
                                                    {invoice_date}
                                                </h6>
                                            </div>
                                        </div>

                                        <div className="row text-md-end text-start">
                                            <div className="col-md-6">
                                                <h6 className="text-secondary font-weight-normal mb-0">
                                                    Create date:
                                                </h6>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="text-dark mb-0">
                                                    {job_createdAt}
                                                </h6>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <br />

                                <div className="row justify-content-md-between">
                                    <div className="col-md-4 mt-auto">
                                        <h6 className="mb-0 text-start text-secondary font-weight-normal">
                                            Job details
                                        </h6>
                                        <h6 className="text-start mb-0">
                                            {skill?.name} - {skill?.sub_skill?.name}
                                        </h6>
                                        <h6 className="text-start mb-0">
                                            {job_address}
                                        </h6>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="table-responsive">
                                            <table className="table text-right">
                                                <thead>
                                                    <tr>
                                                        <th scope="col" className="pe-2 text-start ps-2">
                                                            Description
                                                        </th>
                                                        <th scope="col" className="pe-2 text-end">
                                                            Sub-Total
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="pe-2 text-start ps-2">
                                                            Booking fee
                                                        </td>
                                                        <td className="ps-4 text-end">
                                                            {formatMoney(job_booking_fee)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pe-2 text-start ps-2">
                                                            Call out fee
                                                        </td>
                                                        <td className="ps-4 text-end">
                                                            {formatMoney(job_call_out_fee)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th className="h5 ps-4">
                                                            Subtotal
                                                        </th>
                                                        <th className="text-end h5 ps-4">
                                                            {formatMoney(subtotal)}
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th className="h5 ps-4">
                                                            VAT ({company?.company_vat}%)
                                                        </th>
                                                        <th className="text-end h5 ps-4">
                                                            {formatMoney(vat_amount)}
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th className="h5 ps-4">
                                                            Total
                                                        </th>
                                                        <th className="text-end h5 ps-4">
                                                            {formatMoney(total)}
                                                        </th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer">
                                <div className="row">
                                    <div className="col-lg-5 text-left"></div>
                                    <div className="col-lg-7 text-md-end mt-md-0 mt-3">
                                        <button
                                            className={`btn btn-sm btn-icon btn-3 btn-${theme.main}`}
                                            type="button"
                                            onClick={handleDownload}
                                            disabled={downloadBtnLoading}
                                        >
                                            {downloadBtnLoading ? (
                                                <BtnSpinner />
                                            ) : (
                                                <>
                                                    <span className="btn-inner--icon">
                                                        <i className="material-icons text-lg">
                                                            download
                                                        </i>
                                                    </span>
                                                    <span className="btn-inner--text">
                                                        Download Invoice
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InstafixToCustomer;